import { useState } from "react";
import SiteHeader from "@/components/SiteHeader";
import SiteFooter from "@/components/SiteFooter";
import { useSettings } from "@/lib/settings";
import { api, extractError } from "@/lib/api";
import { toast } from "sonner";
import { Mail, Phone, MapPin, Send } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";

export default function Contact() {
  const { settings } = useSettings();
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      return toast.error("Nom, email et message sont obligatoires");
    }
    setBusy(true);
    try {
      await api.post("/contact", form);
      setSent(true);
      toast.success("Message envoyé — nous vous répondrons rapidement");
      setForm({ name: "", email: "", subject: "", message: "" });
    } catch (e) {
      toast.error(extractError(e));
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-[#f8f9fa]">
      <SiteHeader />
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-16">
        <div className="text-xs uppercase tracking-[0.2em] text-[#0e7a3f] font-bold">Contact</div>
        <h1 className="font-display text-4xl md:text-5xl font-black text-[#0a1e3f] mt-2">Une question ? Parlons-nous.</h1>
        <div className="mt-10 grid grid-cols-1 lg:grid-cols-5 gap-6">
          <div className="lg:col-span-2 space-y-4">
            <div className="bg-white rounded-3xl p-6 border border-gray-100 mbk-card-shadow">
              <Mail className="h-6 w-6 text-[#0e7a3f]" />
              <div className="font-semibold mt-3 text-[#0a1e3f]">Email</div>
              <div className="text-sm text-gray-600 mt-1">{settings.contact_email || "mobikarafrique@gmail.com"}</div>
              {settings.support_email && <div className="text-xs text-gray-400 mt-1">Support : {settings.support_email}</div>}
            </div>
            {settings.contact_phone && (
              <div className="bg-white rounded-3xl p-6 border border-gray-100 mbk-card-shadow">
                <Phone className="h-6 w-6 text-[#0e7a3f]" />
                <div className="font-semibold mt-3 text-[#0a1e3f]">Téléphone</div>
                <div className="text-sm text-gray-600 mt-1">{settings.contact_phone}</div>
                {settings.whatsapp_number && <div className="text-xs text-gray-400 mt-1">WhatsApp : {settings.whatsapp_number}</div>}
              </div>
            )}
            {settings.address && (
              <div className="bg-white rounded-3xl p-6 border border-gray-100 mbk-card-shadow">
                <MapPin className="h-6 w-6 text-[#0e7a3f]" />
                <div className="font-semibold mt-3 text-[#0a1e3f]">Adresse</div>
                <div className="text-sm text-gray-600 mt-1">{settings.address}</div>
              </div>
            )}
          </div>

          <form onSubmit={submit} className="lg:col-span-3 bg-white rounded-3xl p-6 md:p-8 border border-gray-100 mbk-card-shadow" data-testid="contact-form">
            <div className="font-display text-xl font-black text-[#0a1e3f]">Envoyez-nous un message</div>
            <p className="text-sm text-gray-500 mt-1">Nous répondons sous 24h ouvrées.</p>
            <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
              <F l="Nom complet"><Input data-testid="c-name" value={form.name} onChange={(e) => upd("name", e.target.value)} required /></F>
              <F l="Email"><Input data-testid="c-email" type="email" value={form.email} onChange={(e) => upd("email", e.target.value)} required /></F>
              <div className="md:col-span-2"><F l="Sujet"><Input data-testid="c-subject" value={form.subject} onChange={(e) => upd("subject", e.target.value)} placeholder="Réservation, partenariat, autre…" /></F></div>
              <div className="md:col-span-2"><F l="Message"><Textarea data-testid="c-message" rows={5} value={form.message} onChange={(e) => upd("message", e.target.value)} required placeholder="Comment pouvons-nous vous aider ?" /></F></div>
            </div>
            <div className="mt-6 flex items-center justify-between">
              {sent ? <div className="text-sm text-[#0e7a3f] font-semibold">✓ Message reçu, nous vous recontactons rapidement.</div> : <div />}
              <Button data-testid="c-submit" type="submit" disabled={busy} className="mbk-btn-primary text-white h-11 px-6 rounded-full gap-2">
                <Send className="h-4 w-4" /> {busy ? "Envoi…" : "Envoyer"}
              </Button>
            </div>
          </form>
        </div>
      </div>
      <SiteFooter />
    </div>
  );
}

const F = ({ l, children }) => (<div><Label className="text-xs uppercase tracking-widest text-gray-500 font-bold">{l}</Label><div className="mt-2">{children}</div></div>);
